import { createContext, useContext, useState, type ReactNode } from "react";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useAuth } from "@/hooks/useAuth";
import { alert } from "@/contexts/AlertModalContext";
import inovaone_api from "@/services/inovaone_api";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import { useSeriais } from "../SeriaisContext";

export type CategoriasContextType = {
  categorias: CategoriaItemResponse[];
  pesquisarCategoria: (codigo: string) => CategoriaItemResponse | undefined;
  coletaCategorias: () => Promise<void>;
  criarCategorias: (
    categorias: NewCategoriaItem[],
  ) => Promise<CategoriaItemResponse[] | undefined>;
  coletarCategoriasEspecificas: (
    codigos: string[],
  ) => Promise<CategoriaItemResponse[]>;
  atualizarDescricaoCategoria: (
    codigo: string,
    descricao: string,
  ) => Promise<CategoriaItemResponse | undefined>;
};

export const CategoriasContext = createContext<
  CategoriasContextType | undefined
>(undefined);

export function CategoriasProvider({ children }: { children: ReactNode }) {
  const { setIsLoading } = useGlobalLoader();
  const { seriais, criarSeriais, coletaSeriais } = useSeriais();
  const { usuario } = useAuth();
  const [categorias, setCategorias] = useState<CategoriaItemResponse[]>([]);

  const criarCategorias = async (categorias: NewCategoriaItem[]) => {
    setIsLoading(true);
    try {
      const seriais_para_criar = [];

      for (const categoria of categorias) {
        const serial_existe = seriais.find((s) => s.gtin === categoria.ean);
        if (!serial_existe) {
          seriais_para_criar.push({
            gtin: categoria.ean,
            serial: -1,
            id_conta: usuario?.id_conta || "",
          });
        }
      }
      await criarSeriais(seriais_para_criar);
      const response_criar_categorias = await inovaone_api.post<
        CategoriaItemResponse[]
      >(`/categoria?retornar_criado_repetido=true`, categorias);
      let categorias_retornadas = response_criar_categorias.data;

      // Com retornar_criado_repetido=true o backend devolve a categoria já
      // existente sem alterá-la; se a descrição enviada difere da base,
      // persiste a nova descrição num segundo POST (POST com _id = update).
      const enviadas_por_ean = new Map(categorias.map((c) => [c.ean, c]));
      const para_atualizar = categorias_retornadas
        .filter((cat) => {
          const enviada = enviadas_por_ean.get(cat.ean);
          return (
            enviada &&
            enviada.descricao.trim() !== "" &&
            enviada.descricao.trim() !== cat.descricao.trim()
          );
        })
        .map((cat) => ({
          ...cat,
          descricao: enviadas_por_ean.get(cat.ean)!.descricao.trim(),
        }));

      if (para_atualizar.length > 0) {
        const response_atualizar = await inovaone_api.post<
          CategoriaItemResponse[]
        >(`/categoria`, para_atualizar);
        const atualizadas = new Map(
          response_atualizar.data.map((c) => [c._id, c]),
        );
        categorias_retornadas = categorias_retornadas.map(
          (c) => atualizadas.get(c._id) || c,
        );
      }

      coletaCategorias();
      coletaSeriais();
      return categorias_retornadas;
    } catch (error) {
      let erroMsg = "Erro desconhecido";
      if (error instanceof Error) {
        // Tentar extrair do details se for erro de validação
        const detalhes = (error as any).response?.data?.details;
        if (Array.isArray(detalhes) && detalhes[0]?.error) {
          erroMsg = detalhes[0].error;
        } else {
          erroMsg = error.message;
        }
      }
      alert.error(`Erro ao criar categoria: ${erroMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const pesquisarCategoria = (codigo: string) => {
    return categorias.find((categoria) => categoria.ean === codigo);
  };

  const coletaCategorias = async () => {
    try {
      const response_categorias = await inovaone_api.get<
        CategoriaItemResponse[]
      >(`/categoria/${usuario?.id_conta}/*/*/*/*`);
      let newCategorias = response_categorias.data;
      newCategorias.pop();
      setCategorias(newCategorias);
    } catch (error) {
      console.error("Erro ao coletar categorias:", error);
      alert.error("Erro ao coletar categorias. Tente novamente mais tarde.");
    }
  };

  const coletarCategoriasEspecificas = async (
    codigos: string[],
  ): Promise<CategoriaItemResponse[]> => {
    if (!usuario?.id_conta) return [];
    if (codigos.length === 0) return [];
    setIsLoading(true);
    try {
      const response_pesquisar_gtins = await inovaone_api.post<
        CategoriaItemResponse[]
      >(`/pesquisar-eans/${usuario?.id_conta}`, [{ eans: codigos }]);
      return response_pesquisar_gtins.data;
    } catch (error) {
      console.error("Erro ao coletar categorias específicas:", error);
      alert.error(
        "Erro ao coletar categorias específicas. Tente novamente mais tarde.",
      );
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const atualizarDescricaoCategoria = async (
    codigo: string,
    descricao: string,
  ): Promise<CategoriaItemResponse | undefined> => {
    // Localiza no estado local; se não estiver carregado, busca no backend
    let categoria = pesquisarCategoria(codigo);
    if (!categoria) {
      const encontradas = await coletarCategoriasEspecificas([codigo]);
      categoria = encontradas[0];
    }
    if (!categoria) return undefined;

    const novaDescricao = descricao.trim();
    if (!novaDescricao || categoria.descricao.trim() === novaDescricao) {
      return categoria;
    }

    setIsLoading(true);
    try {
      const response = await inovaone_api.post<CategoriaItemResponse[]>(
        `/categoria`,
        [{ ...categoria, descricao: novaDescricao }],
      );
      const atualizada = response.data[0];
      if (atualizada) {
        setCategorias((prev) =>
          prev.map((c) => (c._id === atualizada._id ? atualizada : c)),
        );
      }
      return atualizada;
    } catch (error) {
      let erroMsg = "Erro desconhecido";
      if (error instanceof Error) {
        const detalhes = (error as any).response?.data?.details;
        if (Array.isArray(detalhes) && detalhes[0]?.error) {
          erroMsg = detalhes[0].error;
        } else {
          erroMsg = error.message;
        }
      }
      alert.error(`Erro ao atualizar categoria: ${erroMsg}`);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CategoriasContext.Provider
      value={{
        categorias,
        pesquisarCategoria,
        coletaCategorias,
        criarCategorias,
        coletarCategoriasEspecificas,
        atualizarDescricaoCategoria,
      }}
    >
      {children}
    </CategoriasContext.Provider>
  );
}

export function useCategorias() {
  const context = useContext(CategoriasContext);
  if (!context) {
    throw new Error(
      "useCategorias deve ser usado dentro de um CategoriasProvider",
    );
  }
  return context;
}
